import * as z from 'zod';

import {
  ApiKeyId,
  CompanyId,
  ExternalEntityId,
  LoanId,
  LoanImportId,
  LoanImportRowId,
  LosId,
  MERSMIN,
  MERSOrgId,
  UserId,
} from '../BrandedIds';
import { CreateLoanRow } from '../validations/loan/CreateLoanRow';
import { MERSCreateFields } from '../validations/loan/sharedFields/MERSImportFields';

export const ImportStatus = z.enum(['PENDING', 'COMPLETED', 'FAILED']);
export type ImportStatus = z.infer<typeof ImportStatus>;

export const ImportType = z.enum([
  'create',
  'transfer',
  'transferRevision',
  'createTransaction',
  'update',
  'updateEscrowSettings',
  'recordPayment',
  'constructionToPermanent',
  'recordEscrowDisbursement',
  'recordTaxAmountReport',
  'recordDrawDisbursement',
  'updateBorrowerAchDetails',
  'updateIdsAndRemittanceData',
  'managePointOfContact',
  'managePointOfContactSpecialist',
  'generateManualStatement',
  'createAdvance',
  'createLoanNote',
  'createLoanFee',
  'cancelLoanFee',
  'createEscrowSettings',
  'cancelMortgageInsurance',
  'bulkDeposit',
  'failNachaPayments',
  'bulkImportPayees',
  'leretaCustomerAudit',
]);
export type ImportType = z.infer<typeof ImportType>;

export const ImportSource = z.enum(['csv', 'api', 'csvV2', 'fileImport']);
export type ImportSource = z.infer<typeof ImportSource>;

export const ImportFile = z.enum(['csv', 'xlsx']);
export type ImportFile = z.infer<typeof ImportFile>;

export interface LoanImport {
  id: LoanImportId;
  companyId: CompanyId;
  userId: UserId;
  status: ImportStatus;
  spaceId?: string;
  fileName: string;
  totalRows: number;
  createdAt: Date;
  updatedAt: Date;
  type: ImportType;
  createdRowsUrl?: string;
  // TODO LESLIE 11/3: Drop default in prisma after migration
  source: ImportSource;
  apiKeyId?: ApiKeyId;
  apiRequestBody?: string;
  fileImportId?: string;
}

export interface LoanImportRow {
  id: LoanImportRowId;
  loanImportId: LoanImportId;
  rowNumber: number;
  losId?: LosId;
  status: ImportStatus;
  reason?: string;
  createdAt: Date;
  updatedAt: Date;
  loanId?: LoanId;
  source: ImportSource;
  fileName: string;
  importedByName: string;
}

// THINGS TO DO WITH THE RAW IMPORT ROW
// First, take the initial object parser
// from the spreadsheet uploader or API — ie, CreateLoanRow.
// Then...
// * Turn date strings into Date
// * Turn ids into branded IDs
// * Trim/strip strings (in the ImportRowsData response below)

export const ImportRow = z
  .object({
    ...CreateLoanRow.shape,
    loanId: z.string().transform((id) => id as LosId),
    entityCompanyId: z
      .string()
      .transform((id) => id as ExternalEntityId)
      .optional(),
    mers: z
      .object({
        ...MERSCreateFields.shape,
        MIN: MERSMIN.optional(),
        orgId: MERSOrgId.optional(),
      })
      .optional(),
  })
  .strict();

export type ImportRow = z.infer<typeof ImportRow>;
